import React, { useEffect } from 'react';
import { Button, DatePicker, Form, Input, Modal, Select } from 'antd';
import dayjs from 'dayjs';
import { SubscriptionLevel } from '../Types/SubscriptionLevel';
import { mapSubscriptionLevel } from '../Mappers/mapSubscriptionLevel';
import { WebApiAdminDtoCreateUserDto } from '../Repository/eventbookyapi';

interface Props {
    visible: boolean;
    onCancel: () => void;
    onSubmit: (values: WebApiAdminDtoCreateUserDto) => Promise<void>;
}

const subscriptionOptions = Object.values(SubscriptionLevel)
    .filter((v) => typeof v === 'number')
    .map((level) => ({
        value: level as SubscriptionLevel,
        label: mapSubscriptionLevel(level as SubscriptionLevel),
    }));

const AdminCreateCompanyForm: React.FC<Props> = ({ visible, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = React.useState(false);

    useEffect(() => {
        if (visible) {
            form.setFieldsValue({
                subscription: SubscriptionLevel.Free,
            });
        } else {
            form.resetFields();
        }
    }, [visible, form]);

    const handleFinish = async (values: any) => {
        setSubmitting(true);
        try {
            await onSubmit({
                companyName: values.companyName,
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                password: values.password,
                subscription: values.subscription,
                nextRenewal: values.nextRenewal ? values.nextRenewal.toDate() : undefined,
            });
            form.resetFields();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={visible}
            title="Skapa företag"
            onCancel={onCancel}
            footer={null}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
            >
                <Form.Item
                    name="companyName"
                    label="Företagsnamn"
                    rules={[{ required: true, message: 'Fyll i företagsnamn' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="email"
                    label="E-post"
                    rules={[
                        { required: true, message: 'Fyll i e-postadress' },
                        { type: 'email', message: 'Ange en giltig e-postadress' }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="firstName"
                    label="Förnamn"
                    rules={[{ required: true, message: 'Fyll i förnamn' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="lastName"
                    label="Efternamn"
                    rules={[{ required: true, message: 'Fyll i efternamn' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="password"
                    label="Lösenord"
                    rules={[{ required: true, message: 'Fyll i lösenord' }]}
                >
                    <Input.Password />
                </Form.Item>

                <Form.Item
                    name="subscription"
                    label="Kontonivå"
                    rules={[{ required: true, message: 'Välj kontonivå' }]}
                >
                    <Select options={subscriptionOptions} />
                </Form.Item>

                <Form.Item
                    name="nextRenewal"
                    label="Nästa förnyelsedatum"
                    getValueProps={(value) => ({ value: value ? dayjs(value) : value })}
                >
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" loading={submitting} style={{ marginRight: 8 }}>
                        Skapa företag
                    </Button>
                    <Button onClick={onCancel}>Avbryt</Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default AdminCreateCompanyForm;
